import * as Phaser from 'phaser';
import type { ProjectileState } from '@rttv/shared';

// Time-based lerp constant for error correction after dead reckoning.
// k=40 ≈ 0.49/frame at 60fps — fast enough to track homing velocity changes.
const LERP_K = 40;

/**
 * Client-side renderer for all ballistic projectiles.
 *
 * Laser:   atlas sprite (laserBlue01 / laserRed01) rotated to match velocity.
 *          Used by Fighter (3 barrels) and Juggernaut (5-shot spread).
 * Pulse:   three small concentric circles, fades as it approaches max range.
 * Torpedo: larger multi-ring blob with a directional tail — teal/yellow.
 * Missile: medium size, with a brighter core and longer tail — orange.
 */
export class ProjectileSprite {
  private gfx: Phaser.GameObjects.Graphics | null = null;
  private img: Phaser.GameObjects.Image | null = null;

  private color:      number;
  private weaponType: string;

  private targetX: number;
  private targetY: number;
  private lastDistanceTraveled = 0;
  private lastMaxRange         = 1;
  private lastVelX             = 0;
  private lastVelY             = 0;

  constructor(
    scene: Phaser.Scene,
    state: ProjectileState,
    color: number,
    weaponType = 'pulse',
  ) {
    this.color      = color;
    this.weaponType = weaponType;
    this.targetX    = state.x;
    this.targetY    = state.y;
    this.lastDistanceTraveled = state.distanceTraveled;
    this.lastMaxRange         = state.maxRange || 1;
    this.lastVelX             = state.velocityX;
    this.lastVelY             = state.velocityY;

    if (weaponType === 'laser') {
      // Fighter fires blue lasers; Juggernaut fires red lasers tinted orange.
      const frame = (color === 0x44aaff) ? 'laserBlue01.png' : 'laserRed01.png';
      this.img = scene.add.image(state.x, state.y, 'sheet', frame);
      this.img.setDepth(12);
      // Tint non-blue variants to match the ship colour.
      if (color !== 0x44aaff) {
        this.img.setTint(color);
      }
      this.updateImgTransform();
    } else {
      this.gfx = scene.add.graphics();
      this.gfx.setPosition(state.x, state.y);
      this.gfx.setDepth(12);
      this.draw();
    }
  }

  onServerUpdate(state: ProjectileState) {
    this.targetX              = state.x;
    this.targetY              = state.y;
    this.lastDistanceTraveled = state.distanceTraveled;
    this.lastMaxRange         = state.maxRange || 1;
    this.lastVelX             = state.velocityX;
    this.lastVelY             = state.velocityY;
    if (this.gfx) this.draw();
    if (this.img) this.updateImgTransform();
  }

  update(delta: number) {
    const dt   = delta / 1000;
    const lerp = 1 - Math.exp(-LERP_K * dt);

    this.targetX += this.lastVelX * dt;
    this.targetY += this.lastVelY * dt;

    if (this.gfx) {
      this.gfx.x = Phaser.Math.Linear(this.gfx.x, this.targetX, lerp);
      this.gfx.y = Phaser.Math.Linear(this.gfx.y, this.targetY, lerp);
    }
    if (this.img) {
      this.img.x = Phaser.Math.Linear(this.img.x, this.targetX, lerp);
      this.img.y = Phaser.Math.Linear(this.img.y, this.targetY, lerp);
    }
  }

  destroy() {
    this.gfx?.destroy();
    this.img?.destroy();
  }

  // ---------------------------------------------------------------------------

  /** Update rotation and alpha of the laser sprite to match velocity. */
  private updateImgTransform() {
    if (!this.img) return;

    const spd = Math.hypot(this.lastVelX, this.lastVelY);
    if (spd > 0.001) {
      // Game rotation convention: 0=up, CW positive — atan2(vx, -vy) gives this.
      // Phaser Image rotation uses the same convention when set with setRotation().
      this.img.setRotation(Math.atan2(this.lastVelX, -this.lastVelY));
    }

    const rawFade = Math.max(0, 1 - this.lastDistanceTraveled / this.lastMaxRange);
    this.img.setAlpha(Math.sqrt(rawFade));
  }

  private draw() {
    this.gfx!.clear();

    const rawFade = Math.max(0, 1 - this.lastDistanceTraveled / this.lastMaxRange);
    const alpha   = Math.sqrt(rawFade);

    if (this.weaponType === 'torpedo') {
      this.drawTorpedo(alpha);
    } else if (this.weaponType === 'missile') {
      this.drawMissile(alpha);
    } else {
      this.drawPulse(alpha);
    }
  }

  private drawPulse(alpha: number) {
    this.gfx!.fillStyle(this.color, alpha * 0.25);
    this.gfx!.fillCircle(0, 0, 11);
    this.gfx!.fillStyle(this.color, alpha * 0.55);
    this.gfx!.fillCircle(0, 0, 5);
    this.gfx!.fillStyle(0xffffff, alpha * 0.90);
    this.gfx!.fillCircle(0, 0, 2);
  }

  private drawTorpedo(alpha: number) {
    this.gfx!.fillStyle(this.color, alpha * 0.20);
    this.gfx!.fillCircle(0, 0, 18);
    this.gfx!.fillStyle(this.color, alpha * 0.55);
    this.gfx!.fillCircle(0, 0, 9);
    this.gfx!.fillStyle(0xffffff, alpha * 0.92);
    this.gfx!.fillCircle(0, 0, 3.5);
    this.drawTail(alpha, 22, 3);
  }

  private drawMissile(alpha: number) {
    this.gfx!.fillStyle(this.color, alpha * 0.22);
    this.gfx!.fillCircle(0, 0, 14);
    this.gfx!.fillStyle(this.color, alpha * 0.60);
    this.gfx!.fillCircle(0, 0, 7);
    this.gfx!.fillStyle(0xffffff, alpha * 0.95);
    this.gfx!.fillCircle(0, 0, 2.5);
    this.drawTail(alpha, 30, 2);
  }

  /**
   * Draw a tapered line behind the projectile along its travel direction.
   * tailLen: pixels, tailWidth: max width at the base.
   */
  private drawTail(alpha: number, tailLen: number, tailWidth: number) {
    const spd = Math.hypot(this.lastVelX, this.lastVelY);
    if (spd < 0.001) return;

    const tx = -(this.lastVelX / spd) * tailLen;
    const ty = -(this.lastVelY / spd) * tailLen;

    this.gfx!.lineStyle(tailWidth, this.color, alpha * 0.45);
    this.gfx!.lineBetween(0, 0, tx, ty);
    this.gfx!.lineStyle(1, 0xffffff, alpha * 0.30);
    this.gfx!.lineBetween(0, 0, tx * 0.5, ty * 0.5);
  }
}
